import type { ScholarshipPlayer, ScholarshipPlayerPosition } from '../../../../types/scholarships';

export type DepthChartCardId =
  | 'LF'
  | 'CF'
  | 'RF'
  | 'SS'
  | '2B'
  | '3B'
  | '1B'
  | 'C'
  | 'DH'
  | 'UTIL';

/**
 * The coordinate space the cards are anchored in.
 *
 * Wider than the field art on purpose. The field SVGs keep their own `viewBox="0 0 1000 800"`
 * and are stretched across this whole box with `preserveAspectRatio="xMidYMid meet"`, so the
 * browser scales them by `min(1400/1000, 800/800) = 1` and centres them — which lands the
 * field at x=200..1200 without a transform. The 200 units either side are where the DH/DP and
 * Utility cards live.
 *
 * `DepthChartCard` divides by these to turn an anchor into a CSS percentage. They were literal
 * `1000`/`800` in that component before the space widened; keep them here so the two cannot
 * drift apart.
 */
export const DIAMOND_VIEWBOX = { width: 1400, height: 800 } as const;

/** Width of the field art itself, centred inside {@link DIAMOND_VIEWBOX}. */
export const FIELD_WIDTH = 1000;

/** Left edge of the centred field art within the wider card space. */
export const FIELD_OFFSET_X = (DIAMOND_VIEWBOX.width - FIELD_WIDTH) / 2;

export interface DepthChartCardSpec {
  id: DepthChartCardId;
  /** Anchor (top-left corner) of the card in card-space units (1400 x 800). */
  anchor: { x: number; y: number };
  /**
   * Card width, minimum height, and the height it may NOT exceed — all in card-space units.
   *
   * `maxHeight` is the fix for deep positions. Cards are absolutely positioned, so a card
   * with thirty players used to grow straight down through whatever sat beneath it. The
   * ceiling is the vertical gap to the next card below, less a margin, so a full card stops
   * short of its neighbour instead of covering it.
   */
  size: { width: number; height: number; maxHeight: number };
  /** Underlying enum values whose players appear in this card. */
  positionsShown: ScholarshipPlayerPosition[];
  /** Enum value sent to the reorder endpoint on drag-end in this card. */
  reorderPosition: ScholarshipPlayerPosition;
  /** True when this card draws from a pool shared with other cards (OF case). */
  sharedPool: boolean;
}

/**
 * The ten depth chart cards: eight over the field, plus DH/DP and Utility flanking it.
 *
 * X ANCHORS CARRY THE FIELD OFFSET. The field art occupies x=200..1200 (see
 * {@link FIELD_OFFSET_X}), so a card that sits over the field is anchored at its position on
 * the field plus 200. Subtract 200 to reason about where a card sits on the field itself.
 *
 * `maxHeight` per card is the distance from its own anchor down to the top of whatever sits
 * beneath it, minus a margin so the two never touch. These are y-distances, so widening the
 * space left them untouched:
 *
 *   LF/RF  y=100 → 3B/1B at y=500   → 360
 *   CF     y=40  → SS/2B at y=380   → 300
 *   SS/2B  y=380 → C at y=640       → 220
 *   3B/1B  y=500 → field foot y=800 → 270
 *   C      y=640 → field foot y=800 → 150
 *
 * Change an anchor and these have to move with it, or a deep card starts overlapping again.
 */
export const DEPTH_CHART_CARDS: DepthChartCardSpec[] = [
  // Outfield — each card draws from its own dedicated position (no shared pool).
  { id: 'LF', anchor: { x: 240,  y: 100 }, size: { width: 240, height: 220, maxHeight: 360 }, positionsShown: ['LF'], reorderPosition: 'LF', sharedPool: false },
  { id: 'CF', anchor: { x: 580,  y: 40  }, size: { width: 240, height: 220, maxHeight: 300 }, positionsShown: ['CF'], reorderPosition: 'CF', sharedPool: false },
  { id: 'RF', anchor: { x: 920,  y: 100 }, size: { width: 240, height: 220, maxHeight: 360 }, positionsShown: ['RF'], reorderPosition: 'RF', sharedPool: false },
  // Infield
  { id: 'SS', anchor: { x: 470,  y: 380 }, size: { width: 210, height: 180, maxHeight: 220 }, positionsShown: ['SS'], reorderPosition: 'SS', sharedPool: false },
  { id: '2B', anchor: { x: 720,  y: 380 }, size: { width: 210, height: 180, maxHeight: 220 }, positionsShown: ['2B'], reorderPosition: '2B', sharedPool: false },
  { id: '3B', anchor: { x: 240,  y: 500 }, size: { width: 230, height: 180, maxHeight: 270 }, positionsShown: ['3B'], reorderPosition: '3B', sharedPool: false },
  { id: '1B', anchor: { x: 930,  y: 500 }, size: { width: 230, height: 180, maxHeight: 270 }, positionsShown: ['1B'], reorderPosition: '1B', sharedPool: false },
  // Battery
  { id: 'C',  anchor: { x: 600,  y: 640 }, size: { width: 200, height: 140, maxHeight: 150 }, positionsShown: ['C'],  reorderPosition: 'C',  sharedPool: false },
  // Flanking the field, in the 200 units of card space either side of it. Nothing sits beneath
  // either one, so both may run most of the height -- unlike the field cards, whose maxHeight is
  // the gap to their neighbour. The DH card reads 'DP' on a softball board; see positionLabel.
  { id: 'DH',   anchor: { x: 20,   y: 120 }, size: { width: 200, height: 200, maxHeight: 620 }, positionsShown: ['DH'],   reorderPosition: 'DH',   sharedPool: false },
  { id: 'UTIL', anchor: { x: 1180, y: 120 }, size: { width: 200, height: 200, maxHeight: 620 }, positionsShown: ['UTIL'], reorderPosition: 'UTIL', sharedPool: false },
];


/**
 * A player's appearance within a single card. `primary` rows are the player's home
 * position — money/value shown, counted in the card aggregate. `secondary` rows are a
 * read-only "borrowed" appearance from the player's secondary position — no value, and
 * excluded from the card aggregate (money counts once, on primary).
 */
export type CardAppearance = {
  player: ScholarshipPlayer;
  appearance: 'primary' | 'secondary';
};

export type CardPlayersMap = Record<DepthChartCardId, CardAppearance[]>;

const emptyCardMap = (): CardPlayersMap => ({
  LF: [], CF: [], RF: [], SS: [], '2B': [], '3B': [], '1B': [], C: [], DH: [], UTIL: [],
});

/** Stable by-name order (last, first, id) for secondary entries, which carry no sort_order. */
const byName = (a: ScholarshipPlayer, b: ScholarshipPlayer): number => {
  if (a.lastName !== b.lastName) return a.lastName < b.lastName ? -1 : 1;
  if (a.firstName !== b.firstName) return a.firstName < b.firstName ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
};

/**
 * Buckets the filtered player set into the 10 cards as per-appearance entries.
 *
 * Each card lists its PRIMARY players first (in the server's sort_order), then any
 * SECONDARY players (those whose secondaryPosition matches the card), name-sorted and
 * read-only. Players whose primary position is outside the diamond (RHP, LHP, or null)
 * are dropped from primary rows — they don't appear on the diamond per plan decision #15 —
 * but a pitcher does NOT get a diamond secondary (same-section rule enforced on write).
 * The input is assumed pre-sorted by (position, sort_order, last_name, first_name, id).
 */
export const bucketPlayersIntoCards = (players: ScholarshipPlayer[]): CardPlayersMap => {
  const result = emptyCardMap();
  for (const card of DEPTH_CHART_CARDS) {
    const primaries: CardAppearance[] = players
      .filter((p) => p.position != null && card.positionsShown.includes(p.position))
      .map((player) => ({ player, appearance: 'primary' as const }));
    const secondaries: CardAppearance[] = players
      .filter(
        (p) =>
          p.secondaryPosition != null && card.positionsShown.includes(p.secondaryPosition),
      )
      .sort(byName)
      .map((player) => ({ player, appearance: 'secondary' as const }));
    result[card.id] = [...primaries, ...secondaries];
  }
  return result;
};

/**
 * Mirrors the server's `(position, sort_order, last_name, first_name, id)` order.
 * Used after the optimistic reorder so the locally re-keyed players sort correctly
 * for re-rendering.
 */
export const diamondSortComparator = (
  a: ScholarshipPlayer,
  b: ScholarshipPlayer,
): number => {
  const ap = a.position ?? '';
  const bp = b.position ?? '';
  if (ap !== bp) return ap < bp ? -1 : 1;
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  if (a.lastName !== b.lastName) return a.lastName < b.lastName ? -1 : 1;
  if (a.firstName !== b.firstName) return a.firstName < b.firstName ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
};

/**
 * Design-space height of one two-line row, and of a card's fixed parts (header, need line,
 * budget line, the "Show all" button and padding). Measured off the fitted field.
 */
const FIELD_ROW_HEIGHT = 49;
const FIELD_CARD_CHROME = 100;

/**
 * How many whole rows a card holds at its place on the field. The fitted field shows exactly
 * that many and puts the rest behind "Show all", so a deep position never ends in a half-cut row
 * with the rest hidden in a scrollbar. At least one, so a card never shows none.
 */
export const fieldRowCapacity = (spec: DepthChartCardSpec): number =>
  Math.max(1, Math.floor((spec.size.maxHeight - FIELD_CARD_CHROME) / FIELD_ROW_HEIGHT));
